import { z } from "zod";
//#region src/admin-app.ts
/**
* The admin app's identity in the shell, and the route families its own nav lists.
*
* The app is first-party (`FIRST_PARTY_APPS` in `@zion/sdk-contracts`, R-71) and mounts at `/admin`,
* the URL the built-in admin pages had. The shell has no `/admin` route of its own since S7 (R-70):
* `AppHost` mounts the app from the registry entry like every other app, and `GET /v1/apps` decides
* which principals see it. {@link ADMIN_APP_ROUTE_FAMILIES} was the rollout switch per route family
* while the slices landed, S2 to S4; since S4 it is the full enum, the published list of the
* families the app serves. Its readers are the app's slice tests, which assert each slice's routes
* are members (R-29, R-72(14)); no production code reads it since S7.
*
* Feature `cloud/dp/requirements/plans/2026-09-01-admin-app-extraction`, rulings R-3, R-4, R-70 and
* R-71, overview §6 rows 8 to 10.
*/
/** The app's `appId` in `app.json` and in the shell's registry entry. */
const ADMIN_APP_ID = "admin-app";
/**
* The app's `shell[0].mountPath` in `app.json`. `admin` is a reserved segment for every third-party
* publish; the CP admits it for this app because `FIRST_PARTY_APPS` names it (R-71). The test in
* `__tests__/admin-app.test.ts` pins this literal to that row, so the two packages cannot disagree.
*/
const ADMIN_APP_MOUNT_PATH = "/admin";
/**
* Every first segment under `/admin` the built-in admin serves today, from the shell's route
* table in `App.tsx`. The enum is closed: a segment outside it is not an admin route and never
* redirects.
*/
const adminRouteFamilySchema = z.enum([
	"users",
	"groups",
	"roles",
	"org",
	"ai-providers",
	"channels",
	"auth-providers",
	"scim-tokens",
	"oauth-clients",
	"platform-status",
	"apps",
	"inbox",
	"audit"
]);
/**
* The families the admin app serves, in the order they landed. Empty until the first slice
* shipped; the lead appends each slice's families when the slice reports them (`00-index.md` §3,
* R-50 as amended). WS-3 (identity, S3) landed first with six families, then WS-2 (access, S2) with three, then
* WS-4 (releases, S4) with four: all 13 members of the enum are served. Until S7 the shell read
* this list to decide which `/admin/*` paths redirected into the app; S7 deleted that redirect
* (R-70), and the app's slice tests are the list's readers. The app's router checks a path's
* first segment against `adminRouteFamilySchema` only. A
* family listed here with no route would land on the app's not-found page (R-29, `security-audit.md`
* chain E step 3); with all 13 members served, that case is empty on this tree.
*/
const ADMIN_APP_ROUTE_FAMILIES = Object.freeze([
	"org",
	"ai-providers",
	"auth-providers",
	"scim-tokens",
	"oauth-clients",
	"platform-status",
	"users",
	"groups",
	"roles",
	"channels",
	"apps",
	"inbox",
	"audit"
]);
//#endregion
export { ADMIN_APP_ID, ADMIN_APP_MOUNT_PATH, ADMIN_APP_ROUTE_FAMILIES, adminRouteFamilySchema };

//# sourceMappingURL=admin-app.js.map